import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { reportError } from '../../utils/errorReporting';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallbackMessage?: string;
  /** Centre the fallback in the full viewport (top-level boundary). */
  fullScreen?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    reportError(error, {
      componentStack: errorInfo.componentStack ?? undefined,
      source: 'boundary',
    });
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const fallbackCard = (
        <div className="bg-red-50 dark:bg-red-900/10 rounded-lg p-8 text-center">
          <h3 className="text-lg font-medium text-danger-text mb-2">
            {this.props.fallbackMessage || 'Something went wrong'}
          </h3>
          <p className="text-danger-text mb-4 text-sm">
            An unexpected error occurred. Please try again
          </p>
          <button
            onClick={this.handleRetry}
            className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Try Again
          </button>
        </div>
      );

      if (this.props.fullScreen) {
        return (
          <div className="min-h-screen bg-bg flex items-center justify-center p-4">
            <div className="max-w-md w-full">{fallbackCard}</div>
          </div>
        );
      }
      return fallbackCard;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
